import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock3 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import myShopClient from '@services/nearlive';

const CHANNEL_ID = 'local-checkout';

const SUPPORT_URLS = {
	overview: 'https://support.unify.near.st/orders-/7JASpDe6FvayPmBe32vX8m/what-is-local-checkout/7LoHHWcPsBnZGEzMX98q2q',
	billing: 'https://support.unify.near.st/billing-and-plan/7JASpDe6FuAZaFs4GGfa5z/how-billing-works/7JASpDe6Fxuy96Vx3uf3zS'
};

/**
 * Local Checkout = Stripe Connect onboarding. The shared backend handler
 * (POST /api/checkout/{shopId}) creates a Stripe Connect account if needed
 * and returns an `accountLink` URL. We redirect the browser to Stripe; Stripe
 * eventually redirects back to the URL the handler registered.
 *
 * The shared handler hardcodes the return path as
 * `/channels/{shopId}/settings/local-checkout?reason=...`, so the router
 * also exposes a top-level bridge route (`LocalCheckoutReturnRedirect`) that
 * forwards that URL to this setup page with the same query params.
 */
export function LocalCheckoutSetup({ channel, shop }) {
	const location = useLocation();
	const { shopPath } = useShopPath();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const reason = new URLSearchParams(location.search).get('reason');
	const stripeAccountReady = Boolean(channel.record?.config?.stripeAccountReady);
	const isPending = channel.pending;

	const connect = async () => {
		setLoading(true);
		setError(null);
		try {
			const client = await myShopClient();
			const { data } = await client.post(`/api/checkout/${shop.id}`);
			if (data?.link) {
				window.location = data.link;
				return;
			}
			throw new Error('Stripe did not return an onboarding link.');
		} catch (err) {
			setError(err?.message || 'Could not start Stripe onboarding.');
			setLoading(false);
		}
	};

	// Stripe's `refresh_url` is hit when the accountLink expires — re-issue a
	// fresh one immediately so the retailer doesn't see a dead end.
	useEffect(() => {
		if (reason === 'stripe_refresh') connect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reason]);

	// Pending + Stripe verified → waiting on Customer Success to finish setup.
	if (isPending && stripeAccountReady) {
		return (
			<div className="space-y-6">
				<div className="flex items-start gap-4">
					<div className="rounded-full bg-action-soft p-3 text-action-soft-foreground">
						<Clock3 className="h-5 w-5" />
					</div>
					<div className="space-y-1.5">
						<h2 className="text-lg font-bold text-foreground">We're getting you set up</h2>
						<p className="text-sm text-muted-foreground">
							Your Stripe account is verified. Customer Success will be in touch if they need anything else — once your
							storefront is configured, Local Checkout will switch on automatically.
						</p>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-3 pt-2">
					<Link
						to={shopPath(`/channels/${CHANNEL_ID}`)}
						className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:opacity-90"
					>
						Back to channel overview <ArrowRight className="h-3.5 w-3.5" />
					</Link>
					<SupportLinks />
				</div>
			</div>
		);
	}

	// Pending but Stripe not yet verified → retailer needs to come back and
	// finish Stripe Connect onboarding.
	if (isPending && !stripeAccountReady) {
		return (
			<div className="space-y-6">
				<div className="flex items-start gap-4">
					<div className="rounded-full bg-action-soft p-3 text-action-soft-foreground">
						<Clock3 className="h-5 w-5" />
					</div>
					<div className="space-y-1.5">
						<h2 className="text-lg font-bold text-foreground">Finish your Stripe account</h2>
						<p className="text-sm text-muted-foreground">
							We've started setting up your Stripe Connect account but Stripe still needs a few more details before payments
							can be enabled. Pick up where you left off below.
						</p>
					</div>
				</div>

				{reason === 'stripe_return' ? (
					<p className="text-sm text-muted-foreground">
						Welcome back from Stripe — we'll wait for Stripe to confirm your details. You can re-open onboarding any time.
					</p>
				) : null}

				{error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}

				<div className="flex flex-wrap items-center gap-3">
					<Button onClick={connect} disabled={loading}>
						{loading ? 'Opening Stripe…' : 'Finish Stripe onboarding'}
						<ArrowRight className="ml-2 h-3.5 w-3.5" />
					</Button>
					<SupportLinks />
				</div>
			</div>
		);
	}

	// No channel yet, or disabled: kick off the Stripe Connect flow.
	return (
		<div className="space-y-6">
			<div className="space-y-2">
				<div className="flex items-center gap-2 text-sm font-semibold text-action">
					<CheckCircle2 className="h-4 w-4" /> Set up payments with Stripe Connect
				</div>
				<h2 className="text-lg font-bold text-foreground">Connect a Stripe account to take payments</h2>
				<p className="text-sm text-muted-foreground">
					Local Checkout uses Stripe Connect — Stripe handles payments and payouts, we handle the storefront. The next screen will
					hand you off to Stripe to confirm your business details and bank account.
				</p>
			</div>

			<ul className="space-y-2 rounded-2xl bg-muted/40 p-5 text-sm text-foreground/80">
				<li>1. We'll open Stripe in this window to verify your business.</li>
				<li>2. Once Stripe confirms your details we'll start finalising checkout on our side.</li>
				<li>3. You'll get an email when payments are live.</li>
			</ul>

			{error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}

			<div className="flex flex-wrap items-center gap-3">
				<Button onClick={connect} disabled={loading}>
					{loading ? 'Opening Stripe…' : 'Continue to Stripe'}
					<ArrowRight className="ml-2 h-3.5 w-3.5" />
				</Button>
				<SupportLinks />
			</div>
		</div>
	);
}

function SupportLinks() {
	return (
		<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
			<a href={SUPPORT_URLS.overview} target="_blank" rel="noreferrer" className="font-semibold text-action hover:underline">
				About Local Checkout
			</a>
			<a href={SUPPORT_URLS.billing} target="_blank" rel="noreferrer" className="font-semibold text-action hover:underline">
				Billing and payments
			</a>
		</div>
	);
}

/**
 * Bridges the legacy Stripe Connect return URL
 * (`/channels/{shopId}/settings/local-checkout?reason=...`) to the unify
 * setup page. Stripe receives the URL when the accountLink is created
 * server-side (`interfaces/library/api-handlers/src/handlers/checkout/create-account.js`)
 * and that handler is shared with retailers-dashboard, so unify can't pick a
 * different path — instead we catch it here and forward.
 *
 * In multi-shop mode, the destination URL must be prefixed with the shopId
 * that initiated the Stripe flow (carried in the legacy URL).
 */
export function LocalCheckoutReturnRedirect() {
	const { legacyShopId } = useParams();
	const { search } = useLocation();
	const { shops, isLoading } = useShop();

	if (isLoading) {
		return (
			<div className="flex h-screen items-center justify-center bg-background">
				<Loader />
			</div>
		);
	}

	const target = shops.length > 1 ? `/${legacyShopId}/channels/${CHANNEL_ID}/setup${search}` : `/channels/${CHANNEL_ID}/setup${search}`;
	return <Navigate to={target} replace />;
}
